"use client";

import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ICampaign } from "@/interface";
import { useEffect, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useForm } from "react-hook-form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { PlusIcon, Trash2Icon, X } from "lucide-react";
import { useFieldArray } from "react-hook-form";
import { createClient } from "@/utils/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRouter } from "next/navigation";
import { Textarea } from "../ui/textarea";
import RandomList from "./random-list";
import LuckyDraw from "./luckydraw";
import DialogTableWinner from "./dialog-table";
import QRCode from "qrcode";

export default function Game({
  campaign,
  noPadding,
  setDate,
}: {
  campaign: ICampaign;
  noPadding?: boolean;
  setDate?: (date: number) => void;
}) {
  const router = useRouter();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [openGiftBox, setOpenGiftBox] = useState(false);
  const [openGift, setOpenGift] = useState(false);
  const [participant, setParticipant] = useState<any>(null);
  const [gift, setGift] = useState<any>(null);
  const [place, setPlace] = useState<any>(0);
  const [savedCode, setSavedCode] = useState<any>([]);
  const [openTable, setOpenTable] = useState(false);
  const [isLoading, setisLoading] = useState(false);
  const [qr, setQr] = useState<string>("");
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
    setError,
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      phone: "",
      code: [{ id: "", status: true }],
      note: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "code",
  });

  const onSubmit = async (data: any) => {
    if (isSubmitting) return;
    const supabase = await createClient();
    let isValid = true;
    const phoneRegex = /^[0-9]{10}$/;
    if (data.phone && !phoneRegex.test(data.phone)) {
      setError("phone", {
        type: "manual",
        message: "Số điện thoại không hợp lệ",
      });
      return;
    }

    if (campaign?.verify?.allow_new_participants) {
      const { data: existing, error: checkError } = await supabase
        .from("participants")
        .select("id")
        .eq("campaign_id", campaign.id)
        .eq("phone", data.phone)
        .maybeSingle();

      if (checkError || existing) {
        setError("phone", {
          type: "manual",
          message: "Số điện thoại này đã đăng ký",
        });
        return;
      }
      // insert participant mới
      const { data: inserted, error } = await supabase
        .from("participants")
        .insert({
          campaign_id: campaign.id,
          name: data.name,
          phone: data.phone,
          address: data.address || null,
          area: data.area || null,
          note: data.note || null,
          code: [], // ban đầu rỗng
          remaining: 0,
          created_at: new Date(),
          updated_at: new Date(),
        })
        .select("*")
        .single();

      if (error) {
        toast({
          title: "Không thể thêm mới",
          description: error.message,
          variant: "destructive",
          style: {
            color: "white",
          },
        });
        return;
      }

      setParticipant(inserted);
      setOpen(false);
      setOpenGiftBox(true);
    } else {
      // not allow new participants
      let participant: any = null;
      let matchedParticipant: any = null;
      // verify phone
      if (data.phone) {
        // Validate phone
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(data.phone)) {
          setError("phone", {
            type: "manual",
            message: "Số điện thoại không hợp lệ",
          });
          return;
        }

        const { data: ps, error } = await supabase
          .from("participants")
          .select("*")
          .eq("campaign_id", campaign.id)
          .eq("phone", data.phone);

        if (error || ps?.length === 0) {
          console.log("phone error");
          isValid = false;
          return setError("phone", {
            type: "manual",
            message: "Số điện thoại không hợp lệ",
          });
        }

        participant = ps || [];
        setParticipant(participant[0]);
      }

      // verify name
      if (data.name) {
        const { data: ps, error } = await supabase
          .from("participants")
          .select("*")
          .eq("campaign_id", campaign.id)
          .eq("name", data.name);

        if (error) {
          console.log("name error");
          isValid = false;
          return setError("name", {
            type: "manual",
            message: "Tên không hợp lệ",
          });
        }

        participant = ps || [];
        setParticipant(participant[0]);
      }

      // verify code
      if (data.code?.length > 0 && campaign?.verify?.apply_allocation.includes("code")) {
        setSavedCode(data.code);

        data.code.forEach((c: any, index: number) => {
          matchedParticipant = participant?.find((p: any) =>
            (p.code || []).some((pc: any) => pc.id.trim() === c.id.trim())
          );
          setParticipant(matchedParticipant);

          if (!matchedParticipant) {
            // code không tồn tại trong bất kỳ participant nào
            setError(`code.${index}.id`, {
              type: "manual",
              message: "Mã dự thưởng không hợp lệ",
            });
            console.log("code error");
            isValid = false;
          } else {
            // code có tồn tại → kiểm tra status
            const pc = matchedParticipant.code.find(
              (pc: any) => pc.id.trim() === c.id.trim()
            );
            if (!pc.status) {
              setError(`code.${index}.id`, {
                type: "manual",
                message: "Mã dự thưởng đã được sử dụng",
              });
              console.log("code error");
              isValid = false;
            }
          }
        });
      } else {
        setSavedCode([{
          id: "",
          status: true
        }]);
      }

      // verify note
      if (data.note) {
        // update note for participant
        const { error } = await supabase
          .from("participants")
          .update({ note: data.note })
          .eq("id", matchedParticipant.id);

        if (error) {
          console.log("note error");
          isValid = false;
          toast({
            title: "Rất tiếc",
            description: "Đã có lỗi xảy ra",
            variant: "destructive",
            style: {
              color: "white",
            },
          });
        }
      }

      if (isValid) {
        // pass
        setOpen(false);
        setOpenGiftBox(true);
        return;
      } else {
        return;
      }
    }
  };

  const handleJoin = () => {
    if (campaign?.type === 3) {
      setOpenGiftBox(true);
      return;
    }

    const today = new Date();
    today.setHours(0,0,0,0);

    const isValid = campaign?.rounds?.some((r: any) => {
      if (!r.status) return false;

      const from = new Date(r.from_at);
      from.setHours(0,0,0,0);

      const to = new Date(r.to_at);
      to.setHours(0,0,0,0);

      return from <= today && today <= to;
    });
    if (isValid) {
      if (campaign?.verify?.status) {
        setOpen(true);
      } else {
        setOpenGiftBox(true);
      }
    } else {
      // no verify
      toast({
        title: "Thông báo",
        description: "Chưa đến thời gian tham gia",
        variant: "destructive",
        style: {
          color: "white",
        },
      });
    }
  };

  const handleGiftClick = async () => {
    if (isLoading) return;
    setisLoading(true);
    if (
      !campaign?.verify?.allow_new_participants &&
      savedCode.filter((item: any) => item.status).length === 0
    ) {
      setisLoading(false);
      toast({
        title: "Rất tiếc",
        description: "Bạn đã sử dụng hết mã dự thưởng",
        variant: "destructive",
        style: {
          color: "white",
        },
      });
      return;
    }
    const supabase = await createClient();
    const qr = participant.qr ? await QRCode.toDataURL(participant.qr) : "";
    setQr(qr)
    const { data, error } = await supabase.rpc(
      campaign?.verify?.allow_new_participants ? "draw_prize" : "draw_prize_2",
      {
        p_campaign_id: campaign.id,
        p_participant_id: participant.id,
        p_code: campaign?.verify?.allow_new_participants
          ? null
          : savedCode[savedCode.findIndex((c: any) => c.status)].id,
      }
    );

    if (error) {
      toast({
        title: "Rất tiếc",
        description: error.message,
        variant: "destructive",
        style: {
          color: "white",
        },
      });
    } else {
      setOpenGiftBox(false);
      setOpenGift(true);
      if (!data) {
        setGift({
          description: campaign.game?.gift_default_description,
          image: campaign.game?.gift_default_image,
        });
      } else {
        setGift(data);
      }
    }
    setDate?.(Date.now());
    setisLoading(false);
  };

  const handleNext = async () => {
    const supabase = await createClient();
    const { data: p, error } = await supabase
      .from("participants")
      .select("*")
      .eq("id", participant.id)
      .eq("campaign_id", campaign.id)
      .single();

    if (campaign?.verify?.apply_allocation.includes("code")){ 
      setSavedCode(
        p.code.filter((c: any) => savedCode.map((i: any) => i.id).includes(c.id))
      );
    } else {
      setSavedCode([])
    }

    if (campaign?.verify?.allow_new_participants){
      setOpenGiftBox(false);
      setOpenGift(false);
    } else {
      setOpenGiftBox(true);
      setOpenGift(false);
    }
    setParticipant(p);
  };

  const handleBack = () => {
    setOpenGiftBox(false);
    setOpenGift(false);
    setOpenTable(true);
  };

  useEffect(() => {
    reset();
  }, [open]);

  return (
    <section
      id="game"
      style={{
        backgroundColor: campaign?.game?.page_background_color,
        color: noPadding ? campaign?.game?.page_background_text_color : "",
      }}
    >
      <div
        className="flex max-w-[1475px] mx-auto min-h-screen py-34"
        style={{ padding: noPadding ? "0" : "" }}
      >
        {/* Banner wrapper */}
        <div className="flex-1 flex items-center justify-center">
          {campaign?.game?.page_banner?.url && (
            <Image
              src={campaign?.game?.page_banner?.url || ""}
              alt="game"
              width={1920} // width gốc của ảnh
              height={1080} // height gốc của ảnh
              className="w-full h-auto"
              unoptimized
            />
          )}
        </div>
        {/* Content */}
        <div className="p-10 flex flex-col gap-5 items-center justify-center">
          <div className="md:w-[500px] flex flex-col gap-5 mx-auto items-center justify-center">
            <div className="editor-wrapper game-content">
              <div
                className="ck-content"
                dangerouslySetInnerHTML={{
                  __html: campaign?.game?.page_content,
                }}
              />
            </div>
            <Dialog open={open}>
              <DialogTrigger>
                {campaign?.game?.page_button?.url ? (
                  <img
                    onClick={handleJoin}
                    src={campaign?.game?.page_button?.url || ""}
                    alt="game-button"
                    className="cursor-pointer h-auto object-contain"
                  />
                ) : (
                  <div className="w-[500px] h-[500px] bg-gray-200"></div>
                )}
              </DialogTrigger>
              <DialogContent
                onInteractOutside={(event) => {
                  event.preventDefault();
                }}
                showCloseButton={false}
                className="lg:p-8 p-6"
                style={{ maxWidth: "525px" }}
              >
                <VisuallyHidden>
                  <DialogTitle>Hidden Title</DialogTitle>
                </VisuallyHidden>
                <div className="text-center editor-wrapper pop-up-content">
                  <div
                    onClick={() => setOpen(false)}
                    className="cursor-pointer flex items-center justify-center bg-gray-100 rounded-full w-7 h-7 absolute top-4 right-4"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </div>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: campaign?.verify?.content,
                    }}
                  />
                </div>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col gap-5"
                >
                  {campaign?.verify?.apply_allocation.includes("phone") && (
                    <div className="flex flex-col gap-1">
                      <Label className="font-semibold text-md">
                        Số điện thoại
                      </Label>
                      <Input
                        type="text"
                        placeholder="Vui lòng nhập"
                        {...register("phone", {
                          required: "Vui lòng nhập số điện thoại",
                        })}
                        className={`h-12 ${
                          errors.phone ? "border-red-500" : ""
                        }`}
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-xs font-medium">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>
                  )}
                  {campaign?.verify?.apply_allocation.includes("name") && (
                    <div className="flex flex-col gap-1">
                      <Label className="font-semibold text-md">Tên</Label>
                      <Input
                        type="text"
                        placeholder="Vui lòng nhập"
                        {...register("name", { required: "Vui lòng nhập tên" })}
                        className={`h-12 ${
                          errors.name ? "border-red-500" : ""
                        }`}
                      />
                      {errors.name && (
                        <p className="text-red-500 text-xs font-medium">
                          {errors.name.message}
                        </p>
                      )}
                    </div>
                  )}
                  {campaign?.verify?.apply_allocation.includes("code") && (
                    <>
                      {fields.map((field, index) => (
                        <div key={field.id} className="flex flex-col gap-1">
                          <Label className="font-semibold text-md">
                            {fields.length > 1
                              ? `Mã dự thưởng ${index + 1}`
                              : "Mã dự thưởng"}
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="text"
                              placeholder="Vui lòng nhập"
                              {...register(`code.${index}.id`, {
                                required: "Vui lòng nhập mã dự thưởng",
                              })}
                              className={`h-12 ${
                                errors.code?.[index]?.id ? "border-red-500" : ""
                              }`}
                            />
                            {fields.length > 1 && (
                              <Trash2Icon
                                className="w-4 h-4 cursor-pointer text-red-500"
                                onClick={() => remove(index)}
                              />
                            )}
                          </div>
                          {errors.code?.[index]?.id && (
                            <p className="text-red-500 text-xs font-medium">
                              {errors.code?.[index]?.id?.message}
                            </p>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                  {campaign?.verify?.apply_allocation.includes("code") &&
                    fields.length < 3 && (
                      <div
                        onClick={() => append({ id: "", status: true })}
                        className="flex items-center justify-center gap-2 cursor-pointer text-red-500"
                      >
                        <PlusIcon className="w-5 h-5" />
                        <Label className="text-md font-semibold cursor-pointer">
                          Thêm mã dự thưởng
                        </Label>
                      </div>
                    )}
                  {campaign?.verify?.apply_allocation.includes("note") && (
                    <div className="flex flex-col gap-1">
                      <Label className="font-semibold text-md">Ghi chú</Label>
                      <Textarea
                        placeholder="Vui lòng nhập"
                        {...register("note")}
                        className={`h-12 ${
                          errors.note ? "border-red-500" : ""
                        }`}
                      />
                      {errors.note && (
                        <p className="text-red-500 text-xs font-medium">
                          {errors.note.message}
                        </p>
                      )}
                    </div>
                  )}
                  <Button
                    type="submit"
                    variant="ghost"
                    className="p-0 mt-3 h-[54px]"
                  >
                    <img
                      src={campaign?.verify?.button?.url || ""}
                      alt="verify-button"
                      className="cursor-pointer h-full object-cover"
                    />
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
      {campaign?.type === 1 && (
        <>
          <Dialog open={openGiftBox} onOpenChange={setOpenGiftBox}>
            <DialogContent
              onInteractOutside={(event) => {
                event.preventDefault();
              }}
              showCloseButton={false}
              className="bg-white p-0 overflow-hidden border-none rounded-2xl lg:max-w-[1000px] lg:h-[750px] w-[calc(100vw-20px)] sm:max-w-[calc(100vw-20px)]"
            >
              <VisuallyHidden>
                <DialogTitle>Hidden Title</DialogTitle>
              </VisuallyHidden>
              <div 
                 style={{
                  backgroundImage: `url(${
                    campaign?.game?.gift_box_background?.url || ""
                  })`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              className="flex flex-col items-center justify-center">
                <div
                  className="text-center editor-wrapper pop-up-content popup-gift-box-content text-white lg:px-16 lg:py-14 px-4 py-8 gap-8 flex flex-col"
                >
                  <div
                    onClick={() => setOpenGiftBox(false)}
                    className="cursor-pointer flex items-center justify-center bg-white rounded-full w-7 h-7 absolute top-4 right-4"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="ck-content">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: campaign?.game?.gift_box_content.replace(
                          "{{round}}",
                          campaign?.verify?.allow_new_participants
                            ? 1
                            : savedCode.filter((item: any) => item.status)
                                .length || 0
                        ),
                      }}
                    />
                  </div>
                  <div className="flex justify-around lg:gap-10 gap-2 md:max-w-[80%] mx-auto">
                    {campaign?.game?.gift_box.map((item: any, index: number) => {
                      return (
                        <div
                          onClick={handleGiftClick}
                          key={index}
                          className="cursor-pointer"
                        >
                          <img src={item.url} alt="gift" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={openGift} onOpenChange={setOpenGift}>
            <DialogContent
              onInteractOutside={(event) => {
                event.preventDefault();
              }}
              showCloseButton={false}
              style={{ backdropFilter: "none" }}
              className="max-w-[auto] p-0 overflow-hidden border-none rounded-2xl outline-none bg-transparent backdrop-filter-none shadow-none"
            >
              <VisuallyHidden>
                <DialogTitle>Hidden Title</DialogTitle>
              </VisuallyHidden>
              <div
                className="relative text-center editor-wrapper text-white px-6 py-8 lg:min-h-[750px]"
              >

<div 
                  style={{
                    backgroundImage: `url(${campaign?.game?.gift_background?.url || ""})`,
                    backgroundPosition: "top",
                  }}
                  className="absolute top-0 left-0 right-0 bottom-0 w-full h-full"
                />
                <div
                  onClick={() => {
                    setOpenGift(false);
                    setisLoading(false);
                  }}
                  className="cursor-pointer flex items-center justify-center bg-white rounded-full w-7 h-7 absolute top-4 right-4"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </div>
                <div className="flex flex-col items-center justify-between h-full gap-4 pt-12 relative z-10">
                  <img
                    src={gift?.image.url || ""}
                    alt="prize"
                    className="max-w-[300px]"
                  />
                  <div className="editor-wrapper">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: gift?.description
                          .replace("{{name}}", participant?.name)
                          .replace("{{phone}}", participant?.phone)
                          .replace(
                            "{{code}}",
                            savedCode[savedCode.findIndex((c: any) => c.status)]
                              ?.id
                          ),
                      }}
                    />
                    {
                      participant?.qr && (
                        <img src={qr} alt="qr" className="w-20 h-20 my-4 mx-auto"/>
                      )
                    }
                  </div>
                  <div className="flex gap-4">
                    <div onClick={() => {
                      setOpenGiftBox(false);
                      setOpenGift(false);
                      setDate?.(Date.now());
                      router.push(`/#winner-list`);
                    }} className="cursor-pointer">
                      <img
                        src={campaign?.game?.gift_button?.[0]?.url || ""}
                        alt="gift"
                      />
                    </div>
                    <div onClick={handleNext} className="cursor-pointer">
                      <img
                        src={campaign?.game?.gift_button?.[1]?.url || ""}
                        alt="gift"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
      {campaign?.type === 2 && (
        <LuckyDraw
          setDate={setDate}
          savedCode={savedCode}
          setSavedCode={setSavedCode}
          setParticipant={setParticipant}
          campaign={campaign}
          openGiftBox={openGiftBox}
          setOpenGiftBox={setOpenGiftBox}
          handleBack={handleBack}
          participant={participant}
          openTable={openTable}
          setOpenTable={setOpenTable}
        />
      )}
      {campaign?.type === 3 && (
        <RandomList
          setDate={setDate}
          campaign={campaign}
          openGiftBox={openGiftBox}
          setOpenGiftBox={setOpenGiftBox}
          handleBack={handleBack}
          openTable={openTable}
          setOpenTable={setOpenTable}
        />
      )}
      <DialogTableWinner
        campaign={campaign}
        openTable={openTable}
        setOpenTable={setOpenTable}
      />
    </section>
  );
}

